import { useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to manage theme preference with localStorage persistence 
 * 
 * Priority order:
 * 1. Value from navigation state (location.state?.isDark) - allows dynamic theme changes when navigating between screens
 * 2. Value from localStorage - persists user preference across sessions
 * 3. Default to false (light mode) if neither is set
 * 
 * @returns {[boolean, Function]} - [isDark, setIsDark] where isDark
 */
const useTheme = () => {
    const location = useLocation();
    const THEME_KEY = 'digitalLinq_theme_preference';

    // Initialize theme from localStorage or default to light if not set
    const getInitialTheme = () => {
        // First check if theme is passed via navigation state (allowing dynamic theme changes when navigating between screens)
        if (location.state?.isDark !== undefined) {
            return location.state.isDark;
        }
        
        // Then check localStorage for saved theme preference
        const savedTheme = localStorage.getItem(THEME_KEY);
        if (savedTheme !== null) {
            return savedTheme === 'dark';
        }
        
        // Default to light theme if no preference is found
        return false;
    };

    const [isDark, setIsDarkState] = useState(getInitialTheme);

    // Custom setter that also saves to localStorage and accepts both boolean and functional updates
    const setIsDark = (value) => {
        const newValue = typeof value === 'function' ? value(isDark) : value;
        setIsDarkState(newValue);
        localStorage.setItem(THEME_KEY, newValue ? 'dark' : 'light');
    };

    return [isDark, setIsDark];
};

export default useTheme;
